<template>
  <vddl-list
    :id="id"
    ref="plank"
    class="plank"
    :class="classObject"
    :style="styles"
    :list="tlist"
    :allowed-types="allowedTypes"
    :drop="onElementDrop"
    :disable-if="!canEdit"
  >
    <VDropdown
      v-bind="AssetOverlayOptions"
      ref="popover"
      :shown="isPopoverActive"
      :auto-hide="!isSuggestionActive && autoHide"
      @auto-hide="onAutoHide"
    >
      <div
        ref="element"
        v-touch="onTouch"
        class="wrapper"
        @contextmenu="onContextMenu"
        @mousedown="onMouseDown"
        @mouseup="onMouseUp"
        @keydown.tab.prevent="onTab"
      >
        <div ref="top" class="top">
          <div
            v-if="isBoardDraggedOver == true && !element.linkedBoard"
            class="folder placeholder"
          >
            <svg
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
              x="0px"
              y="0px"
              viewBox="0 0 24 24"
              style="enable-background: new 0 0 24 24"
              xml:space="preserve"
              fill="#db841e"
            >
              <path
                d="M12,14L0,0h24L12,14z M24,10h-5.3L12,18l-6.7-8H0l12,14L24,10z"
              />
            </svg>
          </div>
          <div
            v-if="isStartingElement"
            v-tooltip="{
              content: 'Play mode start',
              placement: 'top',
              delay: delayOptions,
            }"
            class="start"
          >
            <svg-icon
              :width="'18'"
              :height="'18'"
              icon="app-icons/starting-element"
              fill="#db841e"
            />
          </div>
          <div
            v-if="element.linkedBoard != null"
            class="folder tooltip-target b3"
            @dblclick="onDblClickLinkedBoard"
          >
            <VDropdown
              distance="15"
              placement="top"
              :triggers="['hover']"
              :delay="{ show: 800, hide: 100 }"
            >
              <svg
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink"
                x="0px"
                y="0px"
                viewBox="0 0 24 24"
                style="enable-background: new 0 0 24 24"
                xml:space="preserve"
                fill="#db841e"
              >
                <path
                  d="M12,14L0,0h24L12,14z M24,10h-5.3L12,18l-6.7-8H0l12,14L24,10z"
                />
              </svg>
              <template #popper>
                <small>Linked to</small>
                <div class="boardname">
                  {{ linkedBoardName }}
                </div>
              </template>
            </VDropdown>
          </div>
          <rich-text-editor
            ref="titleEditor"
            class="title"
            :content="element.title"
            :editable="canEdit"
            :activate-on-double-click="true"
            :extension-options="titleExtensionOptions"
            @focus="onFocusTitle"
            @blur="onBlurTitle"
            @update="onUpdateTitle"
          />
          <VDropdown
            v-if="
              Array.isArray(element.attributes) && element.attributes.length > 0
            "
            distance="13"
            placement="right"
            :triggers="['hover']"
            :delay="{ show: 500, hide: 100 }"
            popper-class="element-attr-popover"
          >
            <div
              class="folder end"
              style="cursor: default"
              @dblclick="setElementPropertiesModal({ value: element.id })"
            >
              <svg-icon
                icon="app-icons/element-properties"
                width="18"
                height="18"
              />
            </div>
            <template #popper>
              <div class="title">
                {{ element.attributes.length }} ATTRIBUTE{{
                  element.attributes.length > 1 ? 'S' : ''
                }}
              </div>
              <div class="el-attributes">
                <div
                  v-for="(attr, index) in attributes"
                  :key="`${_uid}-${index}`"
                  class="el-attribute"
                >
                  <svg-icon
                    :icon="getAttributeIcon(attr)"
                    width="12"
                    height="12"
                    fill="#586163"
                  />
                  <span>{{
                    attr.name ? attr.name : 'Untitled Attribute'
                  }}</span>
                </div>
              </div>
            </template>
          </VDropdown>
        </div>
        <div
          v-if="coverUrl || icon || isCoverLoading"
          class="cover"
          :class="{ isLoading: isCoverLoading }"
        >
          <div
            ref="cover"
            v-touch="onTouchCover"
            class="cover_handler"
            @dblclick="onDblClickCover"
          />
          <svg-icon
            v-if="isCoverLoading"
            icon="app-icons/spinner"
            :width="'40'"
            :height="'40'"
          />
          <img v-else-if="coverUrl" ref="coverImage" :src="coverUrl" />
          <svg-icon
            v-else-if="icon"
            :icon="icon"
            :width="'96'"
            :height="'96'"
          />
          <svg-icon
            v-if="cover.type === 'youtube'"
            class="cover-hover-icon"
            icon="app-icons/video-camera"
            :width="'24'"
            :height="'24'"
            fill="#db841e"
          />
        </div>
        <input
          v-if="uploadInput"
          id="element_cover"
          ref="coverUpload"
          class="input-hidden"
          type="file"
          accept="image/jpeg, image/jpg, image/png"
          @change="onUploadCover"
        />
        <component-collection
          v-if="element.components.length > 0"
          :id="id"
          :components="element.components"
          :type="dragTypes.component"
          :list-type="listType"
          :allowed-types="allowedTypes"
          :move-action="moveComponentAction"
          :delete-action="deleteComponentAction"
        />

        <rich-text-editor
          ref="contentEditor"
          class="container"
          :content="element.content"
          :editable="canEdit"
          :activate-on-double-click="true"
          :extension-options="contentExtensionOptions"
          @focus="onFocusContent"
          @blur="onBlurContent"
          @update="onUpdateContent"
        />

        <template
          v-if="
            element.assets &&
            element.assets.audio &&
            Array.isArray(element.assets.audio) &&
            element.assets.audio.length > 0
          "
        >
          <hr style="width: 100%; border: 1px solid #3c4041" />
          <audio-asset-collection
            :id="id"
            :asset-refs="element.assets.audio"
            :type="dragTypes.asset"
            :list-type="listType"
            :allowed-types="allowedTypesAudio"
            :move-action="moveAudioAssetAction"
            :delete-action="deleteAudioAssetAction"
          />
        </template>

        <div
          v-if="canEdit"
          ref="leftTarget"
          class="connection-handler left"
          data-handler="left"
        />
        <div
          v-if="canEdit"
          ref="rightTarget"
          class="connection-handler right"
          data-handler="right"
        />
        <div
          v-if="canEdit"
          ref="topTarget"
          class="connection-handler up"
          data-handler="top"
        />
        <div
          v-if="canEdit"
          ref="bottomTarget"
          class="connection-handler down"
          data-handler="bottom"
        />
      </div>
      <template #popper>
        <div v-if="isPopoverActive">
          <asset-picker
            v-if="isAssetPickerOpen"
            :asset-reference="cover"
            :on-select="onSelect"
            @close="handleClose"
          />
          <AIElementGenerator
            v-if="isAiElementGeneratorActive"
            :active-element-id="id"
            :last-user-prompt="lastUserPromptGenerator"
            @input-changed="handleGeneratorInputChanged"
            @suggestion-changed="handleSuggestionChanged"
            @close="handleClose"
          />

          <AIElementEnhancer
            v-if="isAiElementEnhancerActive"
            :active-element-id="id"
            :last-user-prompt="lastUserPromptEnhancer"
            @input-changed="handleEnhancerInputChanged"
            @suggestion-changed="handleSuggestionChanged"
            @close="handleClose"
          />
        </div>
      </template>
    </VDropdown>
  </vddl-list>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import _ from 'lodash';
import nodeOnTouch from '@/components/mixins/nodeOnTouch.js';
import jspSingleton from '@/lib/jspSingleton.js';
import { useProtect } from '@/composables/useProtect.js';
import {
  SYS_PERMISSIONS,
  PANZOOM,
  DRAG_TYPE,
  DRAG_LIST,
  DRAG_TYPES_NEW,
  FEATURE_FLAGS,
} from '@/constants.js';
import ComponentCollection from './ComponentCollection/ComponentCollection.vue';
import misc from '../mixins/misc.js';
import RichTextEditor from '../all/RichText/RichTextEditor.vue';
import Rect from '../../rect.js';
import Point from '../../point.js';
import SvgIcon from '../all/SvgIcon.vue';
import updater from '../../vuex/project/updater.js';
import requests from '../../vuex/requests.js';
import {
  roundToNearestMultiple,
  getYoutubeThumbnail,
  stripHTML,
} from '../../utils.js';
import AssetPicker from '../all/AssetPicker/AssetPicker.vue';
import AudioAssetCollection from './AudioAssetCollection/AudioAssetCollection.vue';
import AIElementGenerator from '../all/AI/AIElementGenerator/AIElementGenerator.vue';
import AIElementEnhancer from '../all/AI/AIElementEnhancer/AIElementEnhancer.vue';

export default {
  components: {
    AssetPicker,
    'component-collection': ComponentCollection,
    'rich-text-editor': RichTextEditor,
    SvgIcon,
    AudioAssetCollection,
    AIElementGenerator,
    AIElementEnhancer,
  },

  mixins: [misc, nodeOnTouch],

  props: {
    id: {
      type: String,
      default: null,
    },
    virtualSelected: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      isAssetOverlayOpen: false,
      AssetOverlayOptions: {
        boundary: document.body,
        placement: 'right-start',
        triggers: [],
        delay: {
          show: 0,
          hide: 0,
        },
        popperClass: 'no-padding',
        distance: 3,
      },
      resizeProps: {
        start: this.onStartResize,
        resize: this.onResize,
        stop: this.onEndResize,
        handles: 'nw,ne,se,sw,n,s,w,e',
        containment: '.panzoom',
        disabled: false,
      },
      titleExtensionOptions: {
        hardbreak: true,
        bold: true,
        italic: true,
        underline: true,
        horizontalRule: false,
        customLink: true,
        history: true,
        placeholder: {
          text: 'Double click to add title…',
        },
        dropEventHandler: true,
        customMention: true,
      },
      contentExtensionOptions: {
        hardbreak: true,
        bold: true,
        italic: true,
        underline: true,
        horizontalRule: false,
        customLink: true,
        history: true,
        placeholder: {
          text: 'Double click to add content…',
        },
        dropEventHandler: true,
        customMention: true,
        codeBlockHighlight: { toolbarButton: true },
        blockquote: true,
      },
      dragEnter: false,
      // The type of the dragged item
      dragTypes: {
        component: DRAG_TYPE.ELEMENT_COMPONENT,
        asset: DRAG_TYPE.ELEMENT_ASSET_AUDIO,
      },
      listType: DRAG_LIST.ELEMENTS,
      allowedTypes: [
        DRAG_TYPE.COMPONENT,
        DRAG_TYPE.ELEMENT_COMPONENT,
        DRAG_TYPE.SEARCH_COMPONENT,
        DRAG_TYPE.BOARD,
        DRAG_TYPE.ASSET,
        DRAG_TYPE.ELEMENT_ASSET,
        DRAG_TYPE.ELEMENT_ASSET_AUDIO,
      ],
      allowedTypesAudio: [
        DRAG_TYPE.ASSET,
        DRAG_TYPE.ASSET_AUDIO,
        DRAG_TYPE.ELEMENT_ASSET,
        DRAG_TYPE.ELEMENT_ASSET_AUDIO,
      ],
      tlist: [],
      dragCounter: 0,
      uploadInput: false,
      isCoverLoading: false,
      delayOptions: { show: 800, hide: 0 },
      showSelectionsTimer: null,
      minHeight: '121px',
      resizeObserver: null,
      autoHide: true,

      // AI
      lastUserPromptGenerator: '',
      lastUserPromptEnhancer: '',
      isSuggestionActive: false,
      isAiElementGeneratorActive: false,
      isAiElementEnhancerActive: false,
      isAssetPickerOpen: false,
      isPopoverActive: false,
    };
  },

  computed: {
    ...mapGetters('featureFlags', ['getFeatureFlagBySlug']),
    ...mapGetters('user/account', ['getIsLoggedIn']),
    ...mapGetters('project/data/elements', {
      getContainedNodes: 'getContainedNodes',
      getElement: 'getNode',
      getRect: 'getRect',
      getStartingElement: 'getStartingElement',
      getCoverId: 'getCoverId',
      getCover: 'getCover',
      getCoverIcon: 'getCoverIcon',
      getDefaultIcon: 'getDefaultIcon',
    }),
    ...mapGetters('project/data/notes', { getNote: 'getNode' }),
    ...mapGetters('project/data/jumpers', { getJumper: 'getNode' }),
    ...mapGetters('project/data/branches', { getBranch: 'getNode' }),
    ...mapGetters('project/data/comments', { getComment: 'getNode' }),
    ...mapGetters('project/data/assets', ['getMediumImageUrl']),
    ...mapGetters('project/data/attributes', { getAttributes: 'getNodes' }),
    ...mapGetters('project/config/elements', {
      getIsSelected: 'getIsSelected',
      getElementSelection: 'getSelections',
    }),
    ...mapGetters('application/channels', ['getCollaborators']),
    ...mapGetters('project/config/boards', ['getTransform', 'getOpen']),
    ...mapGetters('project/data/boards', { getBoard: 'getNode' }),
    ...mapGetters('application/drag', [
      'getItems',
      'getSource',
      'getTarget',
      'getIsEmpty',
    ]),
    ...mapGetters('project/config', [
      'getGrid',
      'getIsMultiselection',
      'getCursorMode',
    ]),
    ...mapGetters('user/projects', {
      getOpenProject: 'getOpen',
    }),
    ...mapGetters('application/modals', ['getAssetModal']),
    ...mapGetters('application', ['getExactTransform']),
    ...mapGetters('project/data/variables', ['getAllNodes']),
    ...mapGetters('application/contextMenu', {
      getIsEmptyContextMenu: 'getIsEmpty',
    }),

    /**
     * Return the element
     * @return {Object}
     */
    element() {
      return this.getElement(this.id);
    },

    /**
     * Return the element cover (asset reference)
     * @return {Object}
     */
    cover() {
      return this.element.assets?.cover;
    },

    /**
     * Return the element icon
     * @return {String}
     */
    icon() {
      return this.element.assets?.cover?.file;
    },

    /**
     * Return the cover asset id
     * @return {String}
     */
    coverId() {
      return this.cover?.id;
    },

    /**
     * Return the medium image url of the cover
     * @return  {String}
     */
    coverUrl() {
      const elementCover = this.cover;
      if (elementCover?.type === 'youtube') {
        return getYoutubeThumbnail(elementCover.url);
      }
      return this.getMediumImageUrl(this.coverId);
    },

    /**
     * Return the css styles for positioning
     * @returns {Object}  The { left, top, width, height, min-height, --user-selected-color } styles
     */
    styles() {
      const { x, y, width, height, autoHeight } = this.element || {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        autoHeight: false,
      };
      return {
        left: `${x}px`,
        top: `${y}px`,
        width: `${width}px`,
        height: autoHeight ? 'auto' : `${height}px`,
        'min-height': autoHeight ? this.minHeight : 'unset',
        '--user-selected-color': this.selectedByCollaborator?.color,
      };
    },

    /**
     * Return the connection handler css styles for scaling
     * @returns {Object}    The { transform } styles
     */
    // connectionHandlerStyles() {
    //   const distance = 16 / this.scale;
    //   return {
    //     width: `${distance}px`,
    //     height: `${distance}px`,
    //     left: `${-distance}px`,
    //     right: `${-distance}px`,
    //     top: `${-distance}px`,
    //     bottom: `${-distance}px`,
    //     // 'border-radius': `${8 / this.scale}px`,
    //   };
    // },

    /**
     * Return the collaboarator that has selected this
     */
    selectedByCollaborator() {
      const collaborators = this.getCollaborators.filter(
        c =>
          c.selections &&
          c.selections.elements &&
          c.selections.elements.includes(this.id)
      );
      if (collaborators.length > 0) return collaborators[0];
      return null;
    },

    /**
     * Return the current height of the title DOM element
     * @returns {Number}    The title height
     */
    titleHeight() {
      return $(this.$el).find('.top').height();
    },

    /**
     * Return the linked board name (or null)
     * @returns {String}    The linked board name
     */
    linkedBoardName() {
      if (!this.element?.linkedBoard) {
        return null;
      }
      return this.getBoard(this.element.linkedBoard).name;
    },

    /**
     * Return whether the current element is set as the starting element
     * @returns {Boolean}   Whether the current element is the starting element
     */
    isStartingElement() {
      return this.id === this.getStartingElement;
    },

    /**
     * Return the current board transform
     * @returns {Object}    The board transform
     */
    transform() {
      return this.getTransform(this.getOpen);
    },

    /**
     * Return whether the current element is selected
     * @returns {Boolean}   Whether it is selected
     */
    isSelected() {
      return this.getIsSelected(this.id);
    },

    /**
     * Return whether the current element can be resized
     * @returns {Boolean}   Whether it can be resized
     */
    canResize() {
      return this.isSelected;
    },

    /**
     * Return whether there is a component currently dragged over the element
     * @returns {Boolean}
     */
    isComponentDraggedOver() {
      const { type } = this.getItems;
      return (
        this.dragEnter &&
        (type === DRAG_TYPE.COMPONENT ||
          type === DRAG_TYPE.ELEMENT_COMPONENT ||
          type === DRAG_TYPE.SEARCH_COMPONENT)
      );
    },

    /**
     * Return whether there is an asset currently dragged over the element
     * @returns {Boolean}
     */
    isAssetDraggedOver() {
      const { type } = this.getItems;
      return this.dragEnter && type === DRAG_TYPE.ASSET;
    },

    /**
     * Return whether there is a board currently dragged over the element
     * @returns {Boolean}
     */
    isBoardDraggedOver() {
      const { type } = this.getItems;
      return this.dragEnter > 0 && type === DRAG_TYPE.BOARD;
    },

    /**
     * Return whether there is some new item dragged over the
     */
    isNewItemDraggedOver() {
      const { type } = this.getItems;
      return this.dragEnter && DRAG_TYPES_NEW.includes(type);
    },

    classObject() {
      return {
        [this.element.theme]: true,
        'component-drag-over': this.isComponentDraggedOver,
        'asset-drag-over': this.isAssetDraggedOver,
        'newitem-drag-over': this.isNewItemDraggedOver,
        'board-drag-over': this.isBoardDraggedOver,
        'jtk-drag-selected': this.isSelected,
        'ui-resizable': true,
        'ui-resizable-disabled': !this.showHandlers,
        'user-selected': this.selectedByCollaborator !== null,
        'virtual-selected': this.virtualSelected,
      };
    },

    /**
     * Get the scale of the exact transform
     * @returns {Number}
     */
    scale() {
      return this.getExactTransform.scale;
    },

    /**
     * Enable the connection handlers.
     */
    showHandlers() {
      return this.isSelected && !this.getIsMultiselection;
    },

    /**
     * Returns an object the element's attributes
     * @returns {Array} An array of the element's attributes
     */
    attributes() {
      if (
        Array.isArray(this.element.attributes) &&
        this.element.attributes.length > 0
      ) {
        return this.getAttributes(this.element.attributes);
      }
      return [];
    },
  },
  watch: {
    /**
     * Watch change in exact transform to update the popover
     */
    getExactTransform(newVal, oldVal) {
      if (this.isPopoverActive) {
        this.$refs.popover.onResize();
      }
    },

    /**
     * Watch show handlers change for changing resizable state
     */
    showHandlers(newVal, oldVal) {
      console.log(
        'ShowHandlers watcher called for element id:',
        this.id,
        'value:',
        newVal
      );
      if (!this.canEdit) return;
      if (newVal) {
        // Make element resizable
        $(this.$el).resizable(this.resizeProps);
        // Setup event listeners for auto resizing
        $(this.$el)
          .find('.ui-resizable-handle')
          .on('dblclick', () => {
            this.autoresizeHeight();
          });
      } else {
        // Remove event listeners for auto resizing
        $(this.$el).find('.ui-resizable-handle').off('dblclick');
        // Make element unresizable
        $(this.$el).resizable('destroy');
      }
    },

    /**
     * Watch selection change to inform jsplumb and enable-disable resizable.
     */
    isSelected(newVal, oldVal) {
      if (!this.canEdit) return;
      if (newVal) {
        jspSingleton.instance.addToDragSelection(this.$el); // Add it to jsplumb selection
      } else {
        jspSingleton.instance.removeFromDragSelection(this.$el);
      }
    },

    /**
     * Some property in the element style changes
     */
    styles(newVal, oldVal) {
      if (!_.isEqual(newVal, oldVal)) {
        this.$nextTick(() => {
          jspSingleton.instance?.revalidate(this.$el); // we need to repaint the element's connections
        });
      }
    },

    /**
     * There is nothing being dragged
     */
    getIsEmpty(newVal, oldVal) {
      if (newVal) {
        this.dragEnter = false;
      }
    },

    /**
     * The context menu is empty
     */
    getIsEmptyContextMenu(newVal, oldVal) {
      if (newVal) {
        // No context menu - enable auto hide (a bit hacky)
        setTimeout(() => {
          this.autoHide = true;
        }, 100);
      } else {
        // Since we opened the context menu disable auto hide
        this.autoHide = false;
      }
    },

    canEdit(newVal, oldVal) {
      console.log('canEdit getter setting draggable to newVal');
      jspSingleton.instance.setDraggable(this.$el, newVal);
    },
  },

  /**
   * Lifecycle hook, the element has been mounted
   */
  mounted() {
    console.log('BoardElement mounted');
    jspSingleton.instance.setDraggable(this.$el, this.canEdit);
    // Make element draggable and resizable only if it is editable
    if (!this.canEdit) return;

    // These listeners have to be added explicitly since vddl consumes them
    this.$el.addEventListener('dragenter', this.onDragEnter);
    this.$el.addEventListener('dragleave', this.onDragLeave);

    // Add it to jsplumb's list of managed elements to make it draggable
    jspSingleton.instance.manage(this.$el);

    // Make element resizable only if it is editable
    if (this.isSelected) {
      jspSingleton.instance.addToDragSelection(this.$el);
      $(this.$el).resizable(this.resizeProps);
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.recalcAnchorRect();
    });
  },

  /**
   * Lifecycle hook, before destroy
   */
  beforeUnmount() {
    this.$el.removeEventListener('dragenter', this.onDragEnter);
    this.$el.removeEventListener('dragleave', this.onDragLeave);

    // Remove it from jsplumb's list of managed elements
    jspSingleton.instance.unmanage(this.$el, false);
  },

  methods: {
    ...mapActions('project/config/boards', ['setOpen']),
    ...mapActions('application/drag', [
      'setTarget',
      'setItems',
      'setSource',
      'clear',
    ]),
    ...mapActions('application/contextToolbar', [
      'setAnchor',
      'recalcAnchorRect',
    ]),
    ...mapActions('project/data/elements', [
      'moveComponent',
      'deleteComponent',
      'moveAudio',
      'deleteAudio',
    ]),
    ...mapActions('project/data/elements', {
      updateElement: 'customUpdate',
      deleteLinkedBoard: 'deleteLinkedBoard',
      setStartingElement: 'setStartingElement',
      removeCover: 'removeCover',
      setCover: 'setCover',
    }),
    ...mapActions('project/config/elements', {
      selectElement: 'select',
      deselectElement: 'deselect',
    }),
    ...mapActions('project/data/notes', { updateNote: 'customUpdate' }),
    ...mapActions('project/data/jumpers', { updateJumper: 'customUpdate' }),
    ...mapActions('project/data/branches', { updateBranch: 'customUpdate' }),
    ...mapActions('project/data/comments', { updateComment: 'customUpdate' }),
    ...mapActions('project/data/assets', ['createAsset']),
    ...mapActions('project/config', ['deselectAll', 'deleteSelections']),
    ...mapActions('application/contextMenu', [
      'createItem',
      'setCalcPosition',
      'deleteMenu',
      'setOnDeleteMenu',
    ]),
    ...mapActions('application/modals', [
      'setAssetModal',
      'setElementPropertiesModal',
    ]),
    ...mapActions('application/clipboard', ['cut', 'copy', 'paste']),
    ...mapMutations('application/appStates', ['setShowSelections']),

    onAutoHide() {
      // No context menu - enable auto hide (a bit hacky)
      if (this.isSuggestionActive) return;
      this.isPopoverActive = false;
    },

    /**
     * Returns the corresponding icon based on the attribute type
     * @param {Object} attribute The attribute object
     * @returns {string}
     */
    getAttributeIcon(attribute) {
      switch (attribute.value.type) {
        case 'string':
          if (attribute.value.plain) {
            return 'app-icons/attribute-string';
          }
          return 'app-icons/attribute-rich-text';
        case 'component-list':
          return 'app-icons/attribute-component-list';
        case 'asset-list':
          return 'app-icons/attribute-asset-list';
        default:
          return '';
      }
    },

    /**
     * Autoresize element to content height
     */
    autoresizeHeight() {
      console.log('AUTORESIZE CALLED', this.$el.style.height);
      // Set element height to auto
      this.$el.style['min-height'] = this.minHeight;
      this.$el.style.height = 'auto';

      // Update element with the new height from client
      this.updateElement({
        id: this.id,
        props: {
          height: this.$el.clientHeight,
          autoHeight: true,
        },
        apply: true,
      });

      // Repaint element connections
      this.$nextTick(() => {
        jspSingleton.instance?.revalidate(this.$el);
      });
    },

    /**
     * A callback to pass to asset picker (will be executed when the selection changes)
     */
    onSelect(cover, closeOnSelect = false) {
      this.setCover({ id: this.id, cover, apply: true });
      if (closeOnSelect) {
        this.isPopoverActive = false;
      }
    },

    /**
     * Event listener - something is dropped on the element (vddl drop event)
     */
    onElementDrop(event) {
      this.setTarget({
        value: {
          type: DRAG_LIST.ELEMENTS,
          id: this.id,
          index: this.element.components.length,
        },
      });
    },

    /**
     * Event listener - double click element cover asset. .
     */
    onDblClickCover(event) {
      const cover = this.getCover(this.id);
      this.setAssetModal({ value: cover });
    },

    /**
     * Event listener - mouse up event on element. We will handle element selection here.
     * @param {MouseEvent} event
     */
    onMouseUp(event) {
      console.log('onMouseUp event:', event);
      const { target, offsetX, offsetY, button } = event;
      const { clientWidth, clientHeight } = target;

      // Ignore mouse up if occured on a mention, a component from the list or a hyperlink
      if (target.closest('.mention, .comp, a, .audio-ref')) {
        return;
      }

      // Ignore mouse up on scrollbar and scroll
      if (
        target.classList.contains('editor') &&
        (offsetX > clientWidth || offsetY > clientHeight)
      ) {
        return;
      }

      const isDragged = jspSingleton.instance.hasClass(this.$el, 'jtk-drag');
      // Perform the selection sequence if left or right click and user can edit
      if (!isDragged && this.canEdit && button !== 1) {
        if (!this.$el.contains(document.activeElement)) {
          this.removeAllRanges();
        }
        // When Ctrl or Shift pressed toggle selection
        if (event.ctrlKey || event.shiftKey || event.metaKey) {
          if (!this.isSelected) {
            this.selectElement({ id: this.id, apply: true });
          } else {
            this.deselectElement({ id: this.id, apply: true });
          }
        } else if (!this.isSelected) {
          // Clear any selections and select only this element
          this.deselectAll({ apply: true });
          jspSingleton.instance?.clearDragSelection();

          this.selectElement({ id: this.id, apply: true });
        }
      }
    },

    onTouchCover(event) {
      if (event.type === 'doubletap') {
        this.onDblClickCover();
      }
    },

    /**
     * Event listener - tap event on element. We will handle all tap events here.
     */
    // onTap(event) {
    //   console.log('*******SINGLE TAP ELEMENT', event, event.firstTarget);
    //   this.onMouseUp(event);
    //   event.stopPropagation();
    // },

    /**
     * Event listener - press event on element. We will handle all press events here.
     */
    // onPress(event) {
    //   console.log('*****PRESSED ELEMENT', event);
    //   this.onContextMenu(event);
    //   event.stopPropagation();
    // },

    /**
     * Event listener - contextmenu event.
     */
    onContextMenu(event) {
      // Do nothing if user cannot edit
      if (!this.canEdit) return;
      const { contentEditor, titleEditor } = this.$refs;
      // Do nothing if inside rich text field (will be handled by the editor)
      if (
        (contentEditor.$el.contains(event.target) &&
          contentEditor.isEditMode()) ||
        (titleEditor.$el.contains(event.target) && titleEditor.isEditMode())
      )
        return;

      event.preventDefault();

      // Select the element
      if (!this.isSelected) {
        this.deselectAll({ apply: false });
        // Clear the selections from jsplumb
        jspSingleton.instance.clearDragSelection();
        this.selectElement({ id: this.id, apply: true });
      }
      const x = event.clientX || event.changedPointers[0].clientX; // Get alternate clientX/clientY from touch event
      const y = event.clientY || event.changedPointers[0].clientY;
      // Show the context menu
      this.createContextMenu(x, y);
    },

    /**
     * Create a context menu for this element
     * @param {Number} x  The x client coordinate
     * @param {Number} y  The y client coordinate
     */
    async createContextMenu(x, y) {
      // Get the ids of all selected elements
      const ids = this.getElementSelection;

      if (ids.includes(this.id) && ids.length === 1) {
        // Only if this is the only item selected
        this.createItem({
          name: 'Set Cover...',
          icon: null,
          commands: {
            doAction: () => {
              this.isPopoverActive = true;
              this.isAssetPickerOpen = true;
              this.isAiElementGeneratorActive = false;
              this.isAiElementEnhancerActive = false;
              this.uploadInput = true;
              this.deleteMenu();
              this.isAssetOverlayOpen = true;
            },
            isActive: () => true,
            isEnabled: () => true,
          },
        });
      }

      const elementsWithCover = ids.filter(
        id => this.getCoverId(id) || this.getCoverIcon(id) || this.getCover(id)
      );
      if (elementsWithCover && elementsWithCover.length > 0) {
        this.createItem({
          name: 'Remove Cover',
          icon: null,
          commands: {
            doAction: () => {
              // Remove every element's cover
              elementsWithCover.forEach(id => {
                this.removeCover({ id, apply: false });
              });
              updater.complete({ apply: true });
              this.deleteMenu();
            },
            isActive: () => true,
            isEnabled: () => true,
          },
        });
      }

      if (this.element.linkedBoard) {
        this.createItem({
          name: 'Remove linked board',
          icon: null,
          commands: {
            doAction: () => {
              this.deleteLinkedBoard({
                id: this.id,
                boardId: this.element.linkedBoard,
                apply: true,
              });
              this.deleteMenu();
            },
            isActive: () => true,
            isEnabled: () => true,
          },
        });
      }

      if (ids.includes(this.id) && ids.length === 1) {
        // Only if this is the only item selected
        if (!this.isStartingElement) {
          this.createItem({
            name: 'Set Play Mode start',
            icon: null,
            commands: {
              doAction: () => {
                this.setStartingElement({ id: this.id, apply: true });
                this.deleteMenu();
              },
              isActive: () => true,
              isEnabled: () => true,
            },
          });
        } else {
          this.createItem({
            name: 'Remove Play Mode start',
            icon: null,
            commands: {
              doAction: () => {
                this.setStartingElement({ id: null, apply: true });
                this.deleteMenu();
              },
              isActive: () => true,
              isEnabled: () => true,
            },
          });
        }
      }
      this.createItem({
        name: 'Quickstart Play Mode from here',
        icon: null,
        commands: {
          doAction: () => {
            let entryPoint = '';
            if (process.env.MIX_ANALYTICS_ENABLED === 'true') {
              entryPoint = '&entrypoint=quickstart_btn';
            }
            window.open(
              `${window.location.pathname}/play?elem=${this.id}${entryPoint}`,
              '_blank'
            );
          },
          isActive: () => true,
          isEnabled: () => true,
        },
        submenu: null,
      });
      this.createItem({
        name: 'Copy as Jumper',
        icon: null,
        commands: {
          doAction: () => {
            this.copy();
            this.deleteMenu();
          },
          isActive: () => ids.length === 1,
          isEnabled: () => ids.length === 1,
        },
      });
      this.createItem({
        name: 'Properties...',
        icon: null,
        commands: {
          doAction: () => {
            this.setElementPropertiesModal({ value: this.id });
            this.deleteMenu();
          },
          isActive: () => true,
          isEnabled: () => true,
        },
      });
      this.createItem({
        name: 'Color',
        showName: false,
        icon: null,
        commands: {
          doAction: () => {},
          isActive: () => true,
          isEnabled: () => true,
        },
        addon: {
          component: 'color-picker',
          props: {
            selectedColor: this.element.theme,
            onChangeColor: () => {
              this.setShowSelections({ value: false });
              clearTimeout(this.showSelectionsTimer);
              this.showSelectionsTimer = setTimeout(
                () => this.setShowSelections({ value: true }),
                1600
              );
            },
          },
        },
      });
      if (
        this.getIsLoggedIn &&
        this.getFeatureFlagBySlug(FEATURE_FLAGS.AI_ELEMENT_GENERATOR)
      ) {
        this.createItem({
          name: 'Generate Element',
          icon: 'app-icons/sparkles',
          commands: {
            doAction: () => {
              this.autoHide = false;
              this.isPopoverActive = true;
              this.isAiElementGeneratorActive = true;
              this.isAiElementEnhancerActive = false;
              this.isAssetPickerOpen = false;
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
        });
      }
      if (
        this.getIsLoggedIn &&
        this.getFeatureFlagBySlug(FEATURE_FLAGS.AI_ELEMENT_ENHANCER)
      ) {
        this.createItem({
          name: 'Enhance Element',
          icon: 'app-icons/sparkles',
          commands: {
            doAction: () => {
              this.autoHide = false;
              this.isPopoverActive = true;
              this.isAiElementEnhancerActive = true;
              this.isAiElementGeneratorActive = false;
              this.isAssetPickerOpen = false;
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
        });
      }

      this.createItem({
        name: 'Delete selection',
        icon: null,
        commands: {
          doAction: () => {
            this.deleteSelections({ apply: true });
            this.deleteMenu();
          },
          isActive: () => true,
          isEnabled: () => true,
        },
        submenu: null,
      });

      this.setCalcPosition({
        calcPosition: () => {
          return { left: x, top: y };
        },
      });

      this.setOnDeleteMenu({
        onDeleteMenu: () => {
          clearTimeout(this.showSelectionsTimer);
          this.setShowSelections({ value: true });
        },
      });
    },

    // ----------------- Event handlers -----------------
    handleSuggestionChanged(newState) {
      if (!newState) {
        setTimeout(() => {
          this.isSuggestionActive = newState;
        }, 100);
      } else {
        this.isSuggestionActive = newState;
      }
    },
    handleClose() {
      this.isPopoverActive = false;
      this.isAssetPickerOpen = false;
      this.isAiElementGeneratorActive = false;
      this.isAiElementEnhancerActive = false;
    },

    handleGeneratorInputChanged(newValue) {
      this.lastUserPromptGenerator = newValue;
    },
    handleEnhancerInputChanged(newValue) {
      this.lastUserPromptEnhancer = newValue;
    },

    /**
     * Event listener - user picked a file from the cover upload modal box
     */
    onUploadCover(event) {
      const fileList = event.target.files;
      const file = fileList[0];
      this.isCoverLoading = true;
      requests.assets
        .uploadImageFile({
          hash: this.getOpenProject,
          image: file,
          small: { width: 32, height: 32 },
          medium: { width: 480, height: 480 },
        })
        .then(async response => {
          const { filename, name } = response.data;
          const cover = { type: 'image', file: filename, name };
          const assetId = await this.createAsset({
            props: cover,
            apply: false,
          });
          this.setCover({ id: this.id, cover: { id: assetId }, apply: false });
          updater.complete({ apply: true });
        })
        .catch(error => {
          const { detail } = error.response.data;
          if (detail) {
            this.createError({ text: detail });
          }
        })
        .finally(() => {
          this.isCoverLoading = false;
        });
      this.uploadInput = false;
    },

    /**
     * Event listener - double click on the associated board
     */
    onDblClickLinkedBoard(event) {
      this.setOpen({ id: this.element.linkedBoard, apply: true });
    },

    /**
     * Enable or disable the resizeObserver for the element
     * @param {Boolean} enabled If the observer is enabled
     */
    observeHeight(enabled = true) {
      if (enabled) {
        this.resizeObserver.observe(this.$el);
      } else {
        this.resizeObserver.unobserve(this.$el);
      }
    },

    /**
     * Event listener - the content gains the focus (RichTextEditor event)
     */
    onFocusContent(event) {
      jspSingleton.instance.setDraggable(this.$el, false);

      if (this.canEdit) {
        this.setAnchor({
          anchor: () => {
            const labelRect = this.$el.getBoundingClientRect();
            const rect = new Rect(
              labelRect.left,
              labelRect.right,
              labelRect.top,
              labelRect.bottom
            );
            const parentRect = this.$parent.$el.getBoundingClientRect();
            const parentPt = new Point(parentRect.left, parentRect.top);
            rect.offsetRect(parentPt);
            return rect;
          },
        });
        // Enable the resizeObserver for this element
        this.observeHeight();
      }
    },

    /**
     * Event listener - the content loses the focus (RichTextEditor event)
     */
    onBlurContent(content) {
      if (content !== this.element.content) {
        this.updateElement({
          id: this.id,
          props: { content, height: this.$el.clientHeight },
          apply: true,
        });
      }

      // Re-enable element dragging
      jspSingleton.instance.setDraggable(this.$el, true);

      // Disable the resizeObserver for this element
      this.observeHeight(false);
    },

    /**
     * Event listener - the title gains the focus
     */
    onFocusTitle(event) {
      jspSingleton.instance.setDraggable(this.$el, false);

      this.setAnchor({
        anchor: () => {
          const labelRect = this.$el.getBoundingClientRect();
          const rect = new Rect(
            labelRect.left,
            labelRect.right,
            labelRect.top,
            labelRect.bottom
          );
          const parentRect = this.$parent.$el.getBoundingClientRect();
          const parentPt = new Point(parentRect.left, parentRect.top);
          rect.offsetRect(parentPt);
          return rect;
        },
      });

      // Enable the resizeObserver for this element
      this.observeHeight();
    },

    /**
     * Event listener - the title loses the focus (RichTextEditor event)
     */
    onBlurTitle(event) {
      const title = this.$refs.titleEditor.getContent();
      if (title !== this.element.title) {
        this.updateElement({
          id: this.id,
          props: { title },
          apply: true,
        });
      }

      // Re-enable element dragging
      jspSingleton.instance.setDraggable(this.$el, true);

      // Disable the resizeObserver for this element
      this.observeHeight(false);
    },

    /**
     * Event listener - the content is updated (RichTextEditor event)
     */
    onUpdateContent(content) {
      // this.updateElement({
      //   id: this.id,
      //   props: {
      //     content,
      //   },
      //   apply: true,
      // });
    },

    /**
     * Event listener - the title is updated (RichTextEditor event)
     */
    onUpdateTitle(content) {
      // this.updateElement({
      //   id: this.id,
      //   props: {
      //     title: content,
      //   },
      //   apply: true,
      // });
    },

    /**
     * Event listener - dragenter event something is dragged inside the element
     */
    onDragEnter(event) {
      this.dragEnter = true;
    },

    /**
     * Event listener - dragleave event something dragged is leaving the element
     */
    onDragLeave(event) {
      const rect = this.$el.getBoundingClientRect();
      if (
        event.clientY < rect.top ||
        event.clientY >= rect.bottom ||
        event.clientX < rect.left ||
        event.clientX >= rect.right
      ) {
        this.dragEnter = false;
      }
    },

    /**
     * Event hanlder: element mouse down event.
     * @param {MouseEvent} event
     */
    onMouseDown(event) {
      // On middle-click do not start drag
      if (event.button === 1) {
        event.preventDefault();
      }
    },

    /**
     * Event listener - Element starts being resized (jquery ui event)
     */
    onStartResize(event, ui) {
      this.$el.style.removeProperty('min-height');
      this.removeAllRanges();
    },

    /**
     * Event listener - Element is being resized (jquery ui event)
     */
    onResize(event, ui) {
      // The scale
      const { scale } = this.transform;

      // The new size and position
      let { width, height } = ui.size;
      let { left, top } = ui.position;

      // The old size and position
      const { width: oldWidth, height: oldHeight } = ui.originalSize;
      const { left: oldLeft, top: oldTop } = ui.originalPosition;

      // Our minimum values
      const minHeight = this.$refs.top.clientHeight;
      const minWidth = 122;

      // Compute right and bottom values
      const right = left + width;
      const bottom = top + height;

      const oldRight = oldLeft + oldWidth;
      const oldBottom = oldTop + oldHeight;

      const scaledWidth = oldWidth + (width - oldWidth) / scale;
      const scaledLeft = oldLeft + (left - oldLeft) / scale;
      const scaledRight = left + scaledWidth;
      const scaledHeight = oldHeight + (height - oldHeight) / scale;
      const scaledTop = oldTop + (top - oldTop) / scale;
      const scaledBottom = top + scaledHeight;

      // Account for grid snapping
      const { snap, size } = this.getGrid;
      if (snap) {
        if (left !== oldLeft) {
          left = Math.max(roundToNearestMultiple(scaledLeft, size), 0);
          width = oldRight - left;
          // Adjustment for grid lines
          width += 1;
        } else if (right !== oldRight) {
          width = roundToNearestMultiple(scaledRight, size) - left;
          // Adjustment for grid lines
          width += 1;
        }
        if (top !== oldTop) {
          top = Math.max(roundToNearestMultiple(scaledTop, size), 0);
          height = oldBottom - top;
          // Adjustment for grid lines
          height += 1;
        } else if (bottom !== oldBottom) {
          height = roundToNearestMultiple(scaledBottom, size) - top;
          // Adjustment for grid lines
          height += 1;
        }
      } else {
        if (left !== oldLeft) {
          left = Math.max(scaledLeft, 0);
          width = oldRight - left;
        } else {
          width = scaledWidth;
        }
        if (top !== oldTop) {
          top = Math.max(scaledTop, 0);
          height = oldBottom - top;
        } else {
          height = scaledHeight;
        }
      }

      // Constrain size and position
      const dw = width - minWidth;
      const dh = height - minHeight;
      width = Math.max(width, minWidth);
      height = Math.max(height, minHeight);
      width = Math.min(width, 20000 - left);
      height = Math.min(height, 20000 - top);

      if (width === minWidth && left > oldLeft) {
        left = Math.max(left + dw, 0);
      }
      if (height === minHeight && top > oldTop) {
        top = Math.max(top + dh, 0);
      }

      ui.size.width = width;
      ui.size.height = height;
      ui.position.left = left;
      ui.position.top = top;

      ui.helper.width(ui.size.width);
      ui.helper.height(ui.size.height);
      $(ui.helper).css({
        left: `${ui.position.left}px`,
        top: `${ui.position.top}px`,
      });

      jspSingleton.instance.revalidate(ui.helper.context);
    },

    /**
     * Event listener - Element finished being resized (jquery ui event)
     */
    onEndResize(event, ui) {
      this.updateElement({
        id: this.id,
        props: {
          width: ui.size.width,
          height: ui.size.height,
          x: ui.position.left,
          y: ui.position.top,
          autoHeight: false,
        },
        apply: true,
      });

      jspSingleton.instance.revalidate(ui.helper.context);
    },

    /**
     * Event listener - Tab pressed on editor title or on content
     */
    onTab(event) {
      console.log('TAB PRESSED on board element');
      if (this.canEdit) {
        if (this.$refs.titleEditor.isEditMode()) {
          this.$refs.contentEditor.startEditing();
        } else {
          this.$refs.titleEditor.startEditing();
        }
      }
    },

    /**
     * Used as callback to the element's Component Collection.
     */
    moveComponentAction() {
      const source = this.getSource;
      const target = this.getTarget;
      // We use single drag
      const sId = source.ids[0];
      const sIndex = source.indexes[0];
      // In case the target list is the same element list or another element list
      if (target.type === DRAG_LIST.ELEMENTS) {
        // Move this component to another element list
        this.moveComponent({
          sId,
          sIndex,
          tId: target.id,
          tIndex: target.index,
          apply: true,
        });
      } else {
        // Remove the dragged component from the source element list
        this.deleteComponent({ id: sId, index: sIndex, apply: true });
      }
      this.clear();
    },

    /**
     * Used as callback to the element's Component Collection.
     * @param {String}  id    The element id
     * @param {Number}  index The array index to remove
     */
    deleteComponentAction(id, index) {
      // Remove the component from the source element list
      this.deleteComponent({ id, index, apply: true });
    },

    moveAudioAssetAction() {
      console.log('MOVEAUDIOASSETACTION');
      const source = this.getSource;
      const target = this.getTarget;

      const sId = source.ids[0];
      const sIndex = source.indexes[0];

      if (target.type === DRAG_LIST.ELEMENTS) {
        this.moveAudio({
          sId,
          sIndex,
          tId: target.id,
          tIndex: target.index,
          apply: true,
        });
      } else {
        this.deleteAudio({ id: sId, index: sIndex, apply: true });
      }
      this.clear();
    },

    deleteAudioAssetAction(id, index) {
      this.deleteAudio({ id, index, apply: true });
    },
  },
};
</script>

<style scoped lang="scss"></style>

<style lang="scss">
.ProseMirror {
  word-wrap: break-word;

  -webkit-font-variant-ligatures: none;
  font-variant-ligatures: none;
}
.plank .title .editor {
  font-weight: 600;
  font-size: 16px;
  line-height: 1.4;
  color: #cfcfcf;
  background: none;
  min-width: 100%;
  overflow-y: hidden;
}

// .editor_wrapper {
//   flex: 1;
//   height: 100%;

.editor {
  font-size: 14px;
  line-height: 1.5;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 22px;
  flex: 1;

  p {
    margin: 15px 0 0 0;

    &:first-child {
      margin: 0;
    }
  }

  blockquote {
    margin: 20px 0 20px 0;

    &:first-child {
      margin: 0;
    }
  }

  blockquote {
    border-left: 3px solid rgba(255, 255, 255, 0.2);
    color: #fff;
    padding-left: 15px;
    font-style: italic;
  }

  .ProseMirror {
    flex: 1;
    height: 100%;
  }
}
// }

.plank .content .mention-wrapper,
.plank .titletext .mention-wrapper {
  display: inline-block;
}

.plank .content .mention,
.plank .titletext .mention {
  color: #db841e;
  display: inline-block;
  user-select: text;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}

.plank .content .mention.mention-board,
.plank .titletext .mention.mention-board {
  color: #dbab1e;
}

/* CONTENT SCROLLBAR */

.plank > .vddl-placeholder {
  display: none;
}

.plank.component-drag-over .compgroup .vddl-list.comp-drag-over::after {
  display: none;
}

.plank.board-drag-over {
  .compgroup .vddl-placeholder {
    display: none;
  }

  .folder {
    animation: flickerAnimation 1s infinite;
  }
}

@keyframes flickerAnimation {
  /* flame pulses */
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

/* CONTENT SELECTION */

/* .plank .content::selection {
  background: #aaa;
}

.plank .content span::selection {
  background: #aaa;
} */

/* ENDPOINTS */

// .jtk-endpoint.source circle {
//   // transition: all 0.2s;
// }

// .jtk-endpoint.source.jtk-endpoint-connected.jtk-dragging {
//   visibility: hidden !important;
// }

// .jtk-endpoint.source.endpointDrag.jtk-drag {
//   visibility: hidden !important;
// }

.jtk-endpoint.source:hover {
  z-index: 100;
}

// .jtk-endpoint.source:hover circle {
//   // fill: #db841e;
// }

// .jtk-connector .arrow:hover {
//   fill: #db841e !important;
//   stroke: #db841e !important;
// }

// While dragging do not fire any clicking on mentions or links
.plank.jtk-drag .mention,
.plank.jtk-drag a {
  pointer-events: none;
}
</style>
